import { WORKFLOW_FIRST_PATH, type AddStepPresetId, type WorkflowStepType, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { useState } from "react";
import { problemText } from "../problem-model";
import type { PathDef } from "../types";
import { AddStepMenu } from "./add-step-menu";
import { NoteChip } from "./note-chip";
import { PathActions } from "./path-actions";
import { PathNameInput } from "./path-name-input";
import { ProblemBadge } from "./problem-badge";
import { useBuilderTools } from "./builder-tools";
import { useProblems } from "./problems-context";
import { StepRow } from "./step-row";

const B = "engage.workflows.builder";

interface PathCardProps {
  id: string;
  path: PathDef;
  trigger: WorkflowTrigger;
  disabled: boolean;
  onAdd: (type: WorkflowStepType, preset?: AddStepPresetId) => void;
  onReplace: (type: WorkflowStepType, preset?: AddStepPresetId) => void;
  onEdit: (index: number) => void;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}

/** One path: its name and actions, its steps in order with add / edit / move / remove, and what is wrong with it. */
export function PathCard({ id, path, trigger, disabled, onAdd, onReplace, onEdit, onMove, onRemove }: PathCardProps) {
  const { t } = useTranslation();
  const tools = useBuilderTools();
  const problems = useProblems().byPath.get(id) ?? [];
  // Problems of the path itself (a loop) have no step to sit on, so they are written out here.
  const own = problems.filter((problem) => problem.stepId === undefined);
  const [renaming, setRenaming] = useState(false);
  const title = `${t(`${B}.path`, { id })}${path.label ? ` · ${path.label}` : ""}`;
  const stopRenaming = () => {
    setRenaming(false);
    window.requestAnimationFrame(() => { document.getElementById(`path-${id}`)?.focus(); });
  };
  return (
    <section className="rounded-lg border border-line bg-surface" aria-labelledby={`path-${id}`} data-testid={`workflow-path-${id}`} data-path-card={id}>
      <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <h3 id={`path-${id}`} tabIndex={-1} className="min-w-0 truncate text-sm font-bold">
            {renaming ? t(`${B}.path`, { id }) : title}
          </h3>
          {renaming ? (
            <PathNameInput
              pathTitle={t(`${B}.path`, { id })}
              initial={path.label ?? ""}
              className="h-8 w-56 max-w-full text-sm"
              onSave={(name) => { tools?.rename(id, name); stopRenaming(); }}
              onCancel={stopRenaming}
            />
          ) : null}
          <ProblemBadge problems={problems} />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {id === WORKFLOW_FIRST_PATH ? <span className="text-xs text-fg-subtle">{t(`${B}.pathFrom`)}</span> : null}
          <NoteChip pathId={id} pathTitle={title} />
          {disabled ? null : <PathActions id={id} title={title} onRename={() => { setRenaming(true); }} />}
        </div>
      </div>
      <div className="space-y-2 p-3">
        {own.length > 0 ? (
          <ul className="space-y-1 text-xs font-semibold text-warning" data-testid="path-own-problems">
            {own.map((problem) => <li key={`${problem.code}-${problem.where}`}>{problemText(problem, t)}</li>)}
          </ul>
        ) : null}
        {path.steps.length === 0 ? <p className="rounded-md border border-dashed border-line p-3 text-center text-xs text-fg-subtle">{t(`${B}.noSteps`)}</p> : null}
        <ol className="space-y-2">
          {path.steps.map((step, index) => (
            <StepRow key={step.id} step={step} index={index} count={path.steps.length} disabled={disabled} onEdit={onEdit} onMove={onMove} onRemove={onRemove} />
          ))}
        </ol>
        <AddStepMenu trigger={trigger} pathId={id} path={path} disabled={disabled} onAdd={onAdd} onReplace={onReplace} />
      </div>
    </section>
  );
}
